import {
  Stack,
  ToggleButtonGroup,
  Typography,
  Divider,
  ToggleButton,
  Skeleton,
} from '@mui/material';
import { AccountState, type InventoryResource } from '@repo/api-gw-sdk';
import { useEffect, useState } from 'react';

import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { TimeSelectorPopover } from '@/components/date/timeSelectorPopover';
import { SnapshotIndicator } from '@/components/snapshots/SnapshotIndicator';
import { AuthorizationTooltip } from '@/components/tooltips/authorizationTooltip';
import { AccountAsVaultTag } from '@/components/vaults/vaultTag';
import { useDAL } from '@/contexts/useDAL';
import { useRoles } from '@/contexts/useRoles';
import { useSourceAccounts } from '@/contexts/useSourceAccounts';
import { useWorkspace } from '@/contexts/useWorkspace';
import { TIME_FORMAT, type Dayjs } from '@/utils/dayjs';

import { Panels } from '../../../panels';

export const ContinuousSnapshotRow = ({
  resource,
  selectedDate,
  dateRange,
}: {
  resource: InventoryResource;
  selectedDate: Dayjs;
  dateRange: { start: Dayjs; end: Dayjs };
}) => {
  const isLastDayInRange = dateRange.end.isSame(selectedDate, 'day');
  const { isAuthorizedResource } = useRoles();
  const { rightPanel } = useWorkspace();
  const dal = useDAL();
  const { data: restoreAccounts } = dal.cloudAccounts.restore.useList();
  const isAuthorized = isAuthorizedResource(
    'create:restore_resource',
    resource
  );
  const startTime = dateRange.start.isBefore(selectedDate.startOf('day'))
    ? selectedDate.startOf('day')
    : dateRange.start;
  const endTime = dateRange.end.isAfter(selectedDate.endOf('day'))
    ? selectedDate.endOf('day')
    : dateRange.end;
  const [time, setTime] = useState<Dayjs>(endTime);
  useEffect(() => {
    setTime(endTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);
  const [timeType, setTimeType] = useState<
    'latest-available' | 'specific-time'
  >('latest-available');
  const { sourceAccounts } = useSourceAccounts();
  const account = sourceAccounts?.find(
    (account) => account.providerAccountId === resource.providerAccountId
  );

  const onClose = () => {
    rightPanel.setIsOpen(false);
  };

  const onBack = () => {
    rightPanel.setComponent({
      panel: Panels.InventoryInstance,
      props: {
        id: resource.id,
        onClose,
        initialTab: 'eon-snapshots',
      },
    });
  };

  const onRestore = () => {
    rightPanel.setComponent({
      panel: Panels.RestoreRDSPitrWizard,
      props: {
        resource,
        restoreToTime:
          timeType === 'latest-available'
            ? isLastDayInRange
              ? undefined
              : selectedDate.endOf('day').toDate()
            : time.toDate(),
        onClose,
        onBack,
      },
    });
  };

  const sourceAccountAlsoRestore = restoreAccounts?.accounts.some(
    (account) =>
      account.providerAccountId === resource.providerAccountId &&
      account.state === AccountState.ACCOUNT_STATE_CONNECTED
  );

  return (
    <Stack
      gap='24px'
      data-testid='continuous-snapshot-row'
      divider={<Divider sx={{ borderStyle: 'dashed' }} />}
      className='py-[24px] px-[40px] w-full'
    >
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Typography variant='body1'>{`Available recovery points between ${startTime.format(TIME_FORMAT)} and ${endTime.format(TIME_FORMAT)} UTC`}</Typography>
        <ToggleButtonGroup
          exclusive
          value={timeType}
          onChange={(_, value) => {
            if (value === null) {
              return;
            }
            setTimeType(value);
          }}
        >
          <ToggleButton value={'latest-available'}>
            {isLastDayInRange ? 'Latest available' : 'Latest in this day'}
          </ToggleButton>
          <ToggleButton value={'specific-time'}>Specific time</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          gap='100px'
        >
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            gap='12px'
          >
            <SnapshotIndicator backgroundColor={null} isNative={true} />
            {timeType === 'specific-time' ? (
              <TimeSelectorPopover
                minTime={startTime}
                maxTime={endTime}
                hours={time.hour()}
                minutes={time.minute()}
                seconds={time.second()}
                showSeconds={true}
                showUTC={true}
                onChange={(hours, minutes, seconds) =>
                  setTime(
                    selectedDate
                      .hour(hours)
                      .minute(minutes)
                      .second(seconds ?? 0)
                  )
                }
              />
            ) : (
              <Typography>{`${endTime.format(TIME_FORMAT)} UTC`}</Typography>
            )}
          </Stack>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            gap='12px'
          >
            <Typography variant='body1'>Stored in</Typography>
            {account ? (
              <AccountAsVaultTag
                cloudProvider={account.sourceAccountAttributes.cloudProvider}
                region={resource.region}
              />
            ) : (
              <Skeleton variant='text' width={100} height={24} />
            )}
          </Stack>
        </Stack>
        <AuthorizationTooltip
          overrideTitle
          title={
            sourceAccountAlsoRestore
              ? ''
              : 'Restore requires the source account to be connected as a restore account.'
          }
          isAuthorized={isAuthorized}
        >
          <ButtonWithIcon
            text='Restore'
            disabled={!isAuthorized || !sourceAccountAlsoRestore}
            variant='outlined'
            onClick={onRestore}
            icon='material-symbols-settings-backup-restore-rounded'
          />
        </AuthorizationTooltip>
      </Stack>
    </Stack>
  );
};
